import { useCallback, useRef, useState, type DragEvent } from 'react';

export interface FileDropZone {
    isDragOver: boolean;
    dropHandlers: {
        onDragEnter: (e: DragEvent<HTMLElement>) => void;
        onDragOver: (e: DragEvent<HTMLElement>) => void;
        onDragLeave: (e: DragEvent<HTMLElement>) => void;
        onDrop: (e: DragEvent<HTMLElement>) => void;
    };
}

function carriesFiles(e: DragEvent<HTMLElement>): boolean {
    return Array.from(e.dataTransfer.types).includes('Files');
}

// Counts enter/leave pairs: moving over a child fires leave on the parent before enter on the child.
export function useFileDropZone(onFiles: (files: File[]) => void): FileDropZone {
    const [isDragOver, setIsDragOver] = useState(false);
    const depthRef = useRef(0);

    const onDragEnter = useCallback((e: DragEvent<HTMLElement>) => {
        if (!carriesFiles(e)) return;
        e.preventDefault();
        depthRef.current += 1;
        setIsDragOver(true);
    }, []);

    const onDragOver = useCallback((e: DragEvent<HTMLElement>) => {
        if (!carriesFiles(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    }, []);

    const onDragLeave = useCallback((e: DragEvent<HTMLElement>) => {
        if (!carriesFiles(e)) return;
        depthRef.current = Math.max(0, depthRef.current - 1);
        if (depthRef.current === 0) setIsDragOver(false);
    }, []);

    const onDrop = useCallback(
        (e: DragEvent<HTMLElement>) => {
            if (!carriesFiles(e)) return;
            e.preventDefault();
            depthRef.current = 0;
            setIsDragOver(false);
            onFiles(Array.from(e.dataTransfer.files));
        },
        [onFiles]
    );

    return { isDragOver, dropHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop } };
}
